import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import throttle from 'transmute/throttle';
import { INFINITE_SCROLL_CONFIG_TYPES, isInfiniteScrollEnabled } from './config';
import { useScrollToTopButton } from './useScrollToTopButton';
function getInfiniteScrollAction(config, sentinel) {
  if (config.isLoadingPreviousCursor || config.isLoadingNextCursor) {
    //we do not want to do anything if we're already loading something
    return;
  }
  if (!config.hasNextCursor && !config.hasPreviousCursor) {
    //there is no more to load, dont do anything
    return;
  }
  //we put "bottom" first, it means we'll prioritize loading
  //the rows below us over the top if we have to choose between the two
  if (sentinel === 'bottom' && config.hasNextCursor) {
    return 'LOAD_NEXT';
  }
  if (sentinel === 'top' && config.hasPreviousCursor) {
    return 'LOAD_PREV';
  }
  return undefined;
}

// react-hooks/immutability yells at us when we try to access/mutate a prop.
// Its being kinda stupid and not realizing its an element.
// this util just abtracts away the ugly alias in case theres a better way to do this
// we can clean it up in one shot
function setScrollTop(element, value) {
  const el = element;
  el.scrollTop = value;
}

// Manual scroll anchoring for "load previous" pages.
// When loading *next* pages, the browser's native overflow-anchor keeps the viewport stable
// because new rows are appended below. But when rows are prepended *above* the viewport,
// overflow-anchor can't help — the scroll position stays the same while the content shifts down.
// This hook mimics what overflow-anchor would do: snapshot scrollHeight before the load,
// then after React commits the new rows (layout effect, no deps — runs every render),
// measure the height delta and bump scrollTop by that amount so the user doesn't see a jump.
function useLoadPreviousScrollAnchoring(t0) {
  const $ = _c(4);
  const {
    scrollContainerElement
  } = t0;
  const scrollHeightBeforeLoad = useRef(null);
  const scrollContainerElementRef = useRef(scrollContainerElement);
  let t1;
  if ($[0] !== scrollContainerElement) {
    t1 = () => {
      scrollContainerElementRef.current = scrollContainerElement;
      if (!scrollContainerElement || scrollHeightBeforeLoad.current === null) {
        return;
      }
      const delta = scrollContainerElement.scrollHeight - scrollHeightBeforeLoad.current;
      if (delta > 0) {
        setScrollTop(scrollContainerElement, scrollContainerElement.scrollTop + delta);
        scrollHeightBeforeLoad.current = null;
      }
    };
    $[0] = scrollContainerElement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useLayoutEffect(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      var _scrollContainerEleme, _scrollContainerEleme2;
      scrollHeightBeforeLoad.current = (_scrollContainerEleme = (_scrollContainerEleme2 = scrollContainerElementRef.current) === null || _scrollContainerEleme2 === void 0 ? void 0 : _scrollContainerEleme2.scrollHeight) !== null && _scrollContainerEleme !== void 0 ? _scrollContainerEleme : null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      trackLoadPreviousScrollAnchor: t2
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function setupInfiniteScrollListeners({
  el,
  isInitializedRef,
  configRef,
  trackLoadPreviousScrollAnchor
}) {
  //this seems kinda funky at first glance (it is, ngl, but its simple and works)
  //When loading up a table at the middle of the dataset (lets say page 3),
  //the top scroll sentinel will be visible, which would immediately trigger a "LOAD_PREV"
  //If we scroll down say 100px, the sentinel is no longer intersecting.
  //The user would have to scroll up a tad to then trigger a "LOAD_PREV"
  // When starting at page 1 (no previous
  // cursor), we leave scrollTop at 0 so the user sees the top of the table.
  if (!isInitializedRef.current) {
    isInitializedRef.current = true;
    const config = configRef.current;
    if (config.type === INFINITE_SCROLL_CONFIG_TYPES.INFINITE_SCROLL_ENABLED && config.hasPreviousCursor) {
      setScrollTop(el, 100);
    }
  }
  function dispatch(action, config) {
    switch (action) {
      case 'LOAD_NEXT':
        {
          config.onNextCursor();
          break;
        }
      case 'LOAD_PREV':
        {
          var _config$onPreviousCur;
          trackLoadPreviousScrollAnchor();
          (_config$onPreviousCur = config.onPreviousCursor) === null || _config$onPreviousCur === void 0 || _config$onPreviousCur.call(config);
          break;
        }
      default:
        break;
    }
  }
  const observer = new IntersectionObserver(entries => {
    const config = configRef.current;
    if (!isInfiniteScrollEnabled(config)) return;
    const bottomEntry = entries.find(entry => entry.target.getAttribute('data-test-id') === 'infinite-scroll-bottom-sentinel');
    const topEntry = entries.find(entry => entry.target.getAttribute('data-test-id') === 'infinite-scroll-top-sentinel');

    //prioritize bottom — dispatch at most one action per callback
    if (bottomEntry !== null && bottomEntry !== void 0 && bottomEntry.isIntersecting) {
      dispatch(getInfiniteScrollAction(config, 'bottom'), config);
    } else if (topEntry !== null && topEntry !== void 0 && topEntry.isIntersecting) {
      dispatch(getInfiniteScrollAction(config, 'top'), config);
    }
  }, {
    root: el
  });
  el.querySelectorAll('[data-test-id="infinite-scroll-top-sentinel"], [data-test-id="infinite-scroll-bottom-sentinel"]').forEach(sentinel => observer.observe(sentinel));
  return () => {
    observer.disconnect();
  };
}
function useScrollPositionReporter(t0) {
  const $ = _c(5);
  const {
    scrollContainerElement,
    isEnabled,
    configRef
  } = t0;
  let t1;
  let t2;
  if ($[0] !== configRef || $[1] !== isEnabled || $[2] !== scrollContainerElement) {
    t1 = () => {
      if (!isEnabled || !scrollContainerElement) {
        return;
      }
      const el = scrollContainerElement;
      let lastPercentage;
      const handleScroll = throttle(300, () => {
        const config = configRef.current;
        if (!isInfiniteScrollEnabled(config) || !config.onScrollPositionChange) {
          return;
        }
        const {
          scrollTop,
          scrollHeight,
          clientHeight
        } = el;
        const scrollableHeight = scrollHeight - clientHeight;
        const scrollPercentage = scrollableHeight > 0 ? scrollTop / scrollableHeight : 0;
        if (scrollPercentage !== lastPercentage) {
          lastPercentage = scrollPercentage;
          config.onScrollPositionChange(scrollPercentage);
        }
      });
      el.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => el.removeEventListener("scroll", handleScroll);
    };
    t2 = [scrollContainerElement, isEnabled, configRef];
    $[0] = configRef;
    $[1] = isEnabled;
    $[2] = scrollContainerElement;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
}
export function useInfiniteScroll(scrollContainerElement, infiniteScrollConfig) {
  const $ = _c(25);
  let t0;
  if ($[0] !== infiniteScrollConfig) {
    t0 = isInfiniteScrollEnabled(infiniteScrollConfig);
    $[0] = infiniteScrollConfig;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isEnabled = t0;
  const configRef = useRef(infiniteScrollConfig);
  let t1;
  if ($[2] !== infiniteScrollConfig) {
    t1 = () => {
      configRef.current = infiniteScrollConfig;
    };
    $[2] = infiniteScrollConfig;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useLayoutEffect(t1);
  const isInitializedRef = useRef(false);
  let t2;
  if ($[4] !== scrollContainerElement) {
    t2 = {
      scrollContainerElement
    };
    $[4] = scrollContainerElement;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    trackLoadPreviousScrollAnchor
  } = useLoadPreviousScrollAnchoring(t2);
  const t3 = isEnabled ? infiniteScrollConfig.onScrollToTop : undefined;
  let t4;
  if ($[6] !== isEnabled || $[7] !== scrollContainerElement || $[8] !== t3) {
    t4 = {
      scrollContainerElement,
      isEnabled,
      onScrollToTop: t3
    };
    $[6] = isEnabled;
    $[7] = scrollContainerElement;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    ScrollToTopButton
  } = useScrollToTopButton(t4);
  let t5;
  if ($[10] !== isEnabled || $[11] !== scrollContainerElement) {
    t5 = {
      scrollContainerElement,
      isEnabled,
      configRef
    };
    $[10] = isEnabled;
    $[11] = scrollContainerElement;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useScrollPositionReporter(t5);
  const hasNextCursor = isEnabled && "hasNextCursor" in infiniteScrollConfig && infiniteScrollConfig.hasNextCursor;
  const hasPreviousCursor = isEnabled && "hasPreviousCursor" in infiniteScrollConfig && infiniteScrollConfig.hasPreviousCursor;
  let t6;
  if ($[13] !== isEnabled || $[14] !== scrollContainerElement || $[15] !== trackLoadPreviousScrollAnchor) {
    t6 = () => {
      if (!isEnabled || !scrollContainerElement) {
        return;
      }
      return setupInfiniteScrollListeners({
        el: scrollContainerElement,
        isInitializedRef,
        configRef,
        trackLoadPreviousScrollAnchor
      });
    };
    $[13] = isEnabled;
    $[14] = scrollContainerElement;
    $[15] = trackLoadPreviousScrollAnchor;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== hasNextCursor || $[18] !== hasPreviousCursor || $[19] !== isEnabled || $[20] !== scrollContainerElement || $[21] !== trackLoadPreviousScrollAnchor) {
    t7 = [scrollContainerElement, isEnabled, trackLoadPreviousScrollAnchor, hasNextCursor, hasPreviousCursor];
    $[17] = hasNextCursor;
    $[18] = hasPreviousCursor;
    $[19] = isEnabled;
    $[20] = scrollContainerElement;
    $[21] = trackLoadPreviousScrollAnchor;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  useEffect(t6, t7);
  let t8;
  if ($[23] !== ScrollToTopButton) {
    t8 = {
      ScrollToTopButton
    };
    $[23] = ScrollToTopButton;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}